import { Outlet } from 'react-router-dom';
import { ShieldOff, Wrench } from 'lucide-react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useSystemModuleStore } from '@/features/system-modules';
import { Button } from '@/components/ui/button';
import { SYSTEM_ADMIN_ROLE } from '@features/shared/constants/roles';

interface ModuleGuardProps {
  moduleCode: string;
}

/**
 * Wraps a set of routes behind a module availability check.
 * If the module is disabled or under maintenance (and the user cannot bypass),
 * renders an inline notice instead of the page content.
 */
export function ModuleGuard({ moduleCode }: ModuleGuardProps) {
  const hasRole = useAuthStore((s) => s.hasRole);
  const isModuleAccessible = useSystemModuleStore((s) => s.isModuleAccessible);
  const modules = useSystemModuleStore((s) => s.modules);

  const isAdmin = hasRole(SYSTEM_ADMIN_ROLE);
  const accessible = isModuleAccessible(moduleCode, isAdmin);

  if (accessible) {
    return <Outlet />;
  }

  const module = modules.find((m) => m.moduleCode === moduleCode);
  const isDisabled = module && !module.isEnabled;
  const defaultMessage = isDisabled
    ? `${module?.moduleName ?? 'This module'} is currently disabled.`
    : `${module?.moduleName ?? 'This module'} is currently under maintenance.`;
  const message = module?.maintenanceMessage || defaultMessage;

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        {isDisabled ? (
          <ShieldOff className="h-8 w-8 text-destructive" />
        ) : (
          <Wrench className="h-8 w-8 text-warning" />
        )}
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">
          {isDisabled ? 'Module Unavailable' : 'Under Maintenance'}
        </h2>
        <p className="text-muted-foreground max-w-sm">{message}</p>
      </div>
      <Button variant="outline" onClick={() => window.history.back()}>
        Go Back
      </Button>
    </div>
  );
}
